/**
 * WeeklySummaryPanel — three-card overview of what's happening this week.
 * Used by DashboardPage (company-wide) and MyTasksPage (per-user).
 *
 * Driven by the /api/dashboard/summary endpoint response; works with either
 * scope. Hides the anonymous-responses card when in "mine" scope (the data
 * doesn't make sense at user level).
 */
import React from "react";
import { Link } from "react-router-dom";
import { Calendar, ListChecks, AlertTriangle, Users as UsersIcon, Sparkles } from "lucide-react";

const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
};

const has = (arr) => Array.isArray(arr) && arr.length > 0;

export default function WeeklySummaryPanel({ summary, title = "O que está a acontecer agora", subtitle = "Próximos 7 dias · respostas dos últimos 7 dias" }) {
    if (!summary) {
        return (
            <div className="card-flat" data-testid="weekly-summary-loading">
                <div className="label-caps">Resumo da semana</div>
                <p className="text-sm text-[#52525b] mt-4">A carregar...</p>
            </div>
        );
    }
    const isMine = summary.scope === "mine";

    return (
        <section className="space-y-6" data-testid={`weekly-summary-${summary.scope || "company"}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="label-caps flex items-center gap-2"><Sparkles size={14} /> Resumo da semana</div>
                    <h2 className="font-display text-3xl text-[#0d1628] mt-2">{title}</h2>
                </div>
                <span className="text-xs text-[#52525b]">{subtitle}</span>
            </div>

            <div className={`grid gap-6 ${isMine ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
                {/* Upcoming sessions */}
                <div className="card-flat" data-testid="summary-sessions">
                    <div className="label-caps flex items-center gap-2"><Calendar size={12} /> Sessões agendadas</div>
                    <div className="font-display text-4xl text-[#0d1628] mt-3">{summary.upcoming_sessions?.length || 0}</div>
                    {has(summary.upcoming_sessions) ? (
                        <ul className="mt-5 space-y-3">
                            {summary.upcoming_sessions.slice(0, 5).map((s) => (
                                <li key={s.id} className="border-l-2 border-[#b89b6f] pl-3 py-1" data-testid={`summary-session-${s.id}`}>
                                    <Link to={`/app/projects/${s.project_id}`} className="text-sm text-[#0d1628] hover:italic font-medium">
                                        {s.title}
                                    </Link>
                                    <div className="text-xs text-[#52525b] mt-1">
                                        {fmtWhen(s._when)} · {s.project_name}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#52525b] mt-5">
                            {isMine ? "Não está marcado como participante em nenhuma sessão." : "Sem sessões agendadas para os próximos 7 dias."}
                        </p>
                    )}
                </div>

                {/* Upcoming + overdue tasks */}
                <div className="card-flat" data-testid="summary-tasks">
                    <div className="label-caps flex items-center gap-2"><ListChecks size={12} /> Tarefas em vista</div>
                    <div className="flex items-baseline gap-4 mt-3">
                        <div>
                            <div className="font-display text-4xl text-[#0d1628]">{summary.upcoming_tasks?.length || 0}</div>
                            <div className="text-xs text-[#52525b]">próximos 7d</div>
                        </div>
                        {has(summary.overdue_tasks) && (
                            <div className="text-[#dc2626]" data-testid="summary-overdue-badge">
                                <div className="font-display text-2xl flex items-center gap-1"><AlertTriangle size={16} /> {summary.overdue_tasks.length}</div>
                                <div className="text-xs">em atraso</div>
                            </div>
                        )}
                    </div>
                    {has(summary.overdue_tasks) && (
                        <ul className="mt-5 space-y-2" data-testid="summary-overdue-list">
                            {summary.overdue_tasks.slice(0, 3).map((t) => (
                                <li key={t.id} className="border-l-2 border-[#dc2626] pl-3 py-1">
                                    <Link to={`/app/projects/${t.project_id}`} className="text-sm text-[#0d1628] hover:italic">{t.title}</Link>
                                    <div className="text-xs text-[#dc2626] mt-0.5">Atraso desde {fmtWhen(t._when)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {has(summary.upcoming_tasks) && (
                        <ul className="mt-4 space-y-2" data-testid="summary-upcoming-list">
                            {summary.upcoming_tasks.slice(0, 5).map((t) => (
                                <li key={t.id} className="border-l-2 border-[#b89b6f] pl-3 py-1">
                                    <Link to={`/app/projects/${t.project_id}`} className="text-sm text-[#0d1628] hover:italic">{t.title}</Link>
                                    <div className="text-xs text-[#52525b] mt-0.5">{fmtWhen(t._when)} · {t.project_name}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {!has(summary.upcoming_tasks) && !has(summary.overdue_tasks) && (
                        <p className="text-sm text-[#52525b] mt-5">
                            {isMine ? "Não tem tarefas atribuídas nos próximos 7 dias. Bom trabalho." : "Tudo em dia. Sem tarefas pendentes nos próximos 7 dias."}
                        </p>
                    )}
                </div>

                {/* Anonymous responses + phase progress (company scope only) */}
                {!isMine && (
                    <div className="card-flat" data-testid="summary-responses">
                        <div className="label-caps flex items-center gap-2"><UsersIcon size={12} /> Respostas (7 dias)</div>
                        <div className="flex items-baseline gap-6 mt-3">
                            <div>
                                <div className="font-display text-4xl text-[#0d1628]">{summary.responses_7d?.respondents_total || 0}</div>
                                <div className="text-xs text-[#52525b]">respondentes</div>
                            </div>
                            <div>
                                <div className="font-display text-3xl text-[#b89b6f]">{summary.responses_7d?.anonymous || 0}</div>
                                <div className="text-xs text-[#52525b]">anónimas</div>
                            </div>
                        </div>
                        <div className="mt-6 pt-4 border-t border-[#e4e4e7]">
                            <div className="label-caps text-[#52525b] text-[10px]">Progresso de tarefas por fase</div>
                            <ul className="mt-3 space-y-2" data-testid="summary-phase-progress">
                                {(summary.phase_progress || []).map((ph) => (
                                    <li key={ph.phase} className="text-xs">
                                        <div className="flex items-center justify-between text-[#52525b]">
                                            <span>F{ph.phase} · {ph.tasks_done}/{ph.tasks_total}</span>
                                            <span className="text-[#0d1628]">{ph.progress_pct}%</span>
                                        </div>
                                        <div className="h-1 bg-[#e4e4e7] mt-1">
                                            <div className="h-full bg-[#0d1628] transition-all" style={{ width: `${ph.progress_pct}%` }} />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
